/**
 * Formatierung von Zahlen und Daten für die Anzeige.
 *
 * Grundsatz: Es wird FORMATIERT, nicht gerechnet und nicht ergänzt. Fehlende
 * Werte bleiben fehlend (`undefined`) — sie werden nie zu 0, „—" oder „k. A.";
 * ob und wie eine Lücke gezeigt wird, entscheidet die aufrufende Komponente.
 *
 * Locale ist bewusst fest `de-DE`: Die Daten sind amtliche deutsche Register,
 * die Schreibweise gehört zum Inhalt (Punkt als Tausendertrenner, 24-Stunden-
 * Uhr). Bei einer zweiten Locale wandert das in die Locale-Konfiguration.
 */

const LOCALE = "de-DE";

const zahlFormat = new Intl.NumberFormat(LOCALE);
const prozentFormat = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const prozentFormatGenau = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const prozentpunkteFormat = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});
const datumLang = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
});
const datumKurz = new Intl.DateTimeFormat(LOCALE, {
  weekday: "short",
  day: "numeric",
  month: "short",
});
const datumZiffern = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const monatLang = new Intl.DateTimeFormat(LOCALE, { month: "long", year: "numeric" });
const uhrzeit = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit" });
const zeitpunkt = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** 114881 → "114.881" */
export function zahl(value: number | null | undefined): string | undefined {
  return value === null || value === undefined ? undefined : zahlFormat.format(value);
}

/** 25.02 → "25,0 %" (eine Nachkommastelle, wie im Konzept). */
export function prozent(value: number | null | undefined): string | undefined {
  return value === null || value === undefined ? undefined : `${prozentFormat.format(value)} %`;
}

/** 57.04 → "57,04 %" — für amtliche Werte, die genau so veröffentlicht sind. */
export function prozentGenau(value: number | null | undefined): string | undefined {
  return value === null || value === undefined
    ? undefined
    : `${prozentFormatGenau.format(value)} %`;
}

/**
 * 4.53 → "+4,5 Pp.", −1.3 → "−1,3 Pp.", 0 → "0,0 Pp."
 *
 * Prozentpunkte, nicht Prozent — die Einheit steht dran, weil beides
 * verwechselt wird. Das Vorzeichen ist ein Zeichen, keine Farbe und kein
 * Pfeil: Die Richtung wird genannt, nicht bewertet. Das Minus ist das
 * typografische (U+2212), nicht der Bindestrich.
 */
export function prozentpunkte(value: number | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  // Intl setzt den Bindestrich; ein Minus vor einer Zahl ist das Minuszeichen.
  return `${prozentpunkteFormat.format(value).replace(/^-/, "\u2212")} Pp.`;
}

function parse(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** ISO → "2. Juli 2026" */
/**
 * Quadratmeter als Quadratkilometer.
 *
 * Die Daten liefern Quadratmeter, weil die Quelle das tut; die Einheit fuer
 * Menschen ist der Quadratkilometer.
 *
 * EINE Nachkommastelle. Die zweite taeuscht eine Genauigkeit vor, die niemand
 * braucht und die die Quelle fuer diese Aussage nicht hergibt: Ob ein Veedel
 * 12,04 oder 12,0 Quadratkilometer misst, aendert an keiner Frage etwas, die
 * hier gestellt wird.
 */
export function quadratkilometer(qm: number | null | undefined): string | undefined {
  if (qm === null || qm === undefined) return undefined;
  return (qm / 1_000_000).toLocaleString("de-DE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export function datumAusgeschrieben(iso: string | null | undefined): string | undefined {
  const d = parse(iso);
  return d && datumLang.format(d);
}

/** ISO → "23.04.2026" — kurzes Datum für Listenzeilen. */
export function datumKurzKlartext(iso: string | null | undefined): string | undefined {
  const d = parse(iso);
  return d && datumZiffern.format(d);
}

/**
 * "2025-12" → "Dezember 2025"; volles Datum → ebenfalls Monat und Jahr.
 *
 * Für Angaben, deren Quelle nur den Monat hergibt: Die Presse sagt „Anfang
 * Dezember", also steht monatsgenau in den Daten — und dann darf die Anzeige
 * keinen Tag erfinden. Ein unvollständiges Datum wird auf den Monatsersten
 * gelesen, aber NIE als Tag gezeigt.
 */
export function monatAusgeschrieben(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = parse(/^\d{4}-\d{2}$/.test(iso) ? `${iso}-01` : iso);
  return d && monatLang.format(d);
}

/** ISO → "Mi. 2. Sep." — für die Zeitleiste der Termine. */
export function datumZeitleiste(iso: string | null | undefined): string | undefined {
  const d = parse(iso);
  return d && datumKurz.format(d);
}

/** ISO → "15:30" */
export function uhrzeitKurz(iso: string | null | undefined): string | undefined {
  const d = parse(iso);
  return d && uhrzeit.format(d);
}

/** ISO → "20.08.2026, 18:29" — Datenstand mit Uhrzeit. */
export function zeitpunktGenau(iso: string | null | undefined): string | undefined {
  const d = parse(iso);
  return d && zeitpunkt.format(d);
}

/**
 * Kalendertag als stabiler Gruppierungsschlüssel (lokale Zeitzone).
 * Zum Gruppieren, nicht zum Anzeigen.
 */
/**
 * Ein Tag als ISO-Datum: "2026-08-23".
 *
 * MIT fuehrenden Nullen, und genau darum geht es: Nur so laesst sich der Wert
 * als Grenze in einem Zeichenvergleich benutzen — gegen ISO-Zeitstempel, wie
 * eine Datenbank sie sortiert. `tagesSchluessel` taugt dafuer NICHT: Er laesst
 * die Nullen weg ("2026-8-23"), und "2026-8-23" ist zeichenweise groesser als
 * "2026-12-03". Wer ihn als Grenze nimmt, sortiert jede Dezember-Sitzung in die
 * Vergangenheit.
 *
 * Ortszeit, nicht UTC: Ob eine Sitzung heute ist, entscheidet der Kalender vor
 * Ort, nicht der Nullmeridian.
 */
export function isoTag(datum: Date): string {
  const monat = String(datum.getMonth() + 1).padStart(2, "0");
  const tag = String(datum.getDate()).padStart(2, "0");
  return `${datum.getFullYear()}-${monat}-${tag}`;
}

/**
 * Schluessel zum GRUPPIEREN nach Tag — nicht zum Vergleichen.
 *
 * Er laesst fuehrende Nullen weg und taugt deshalb nur fuer Gleichheit. Fuer
 * Bereichsgrenzen: `isoTag`.
 */
export function tagesSchluessel(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return "";
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
